import maplibregl from 'maplibre-gl';

const MAPTILER_KEY = (process.env.REACT_APP_MAPTILER_KEY || '').trim();
const MAPTILER_STYLE_IDS = {
  default: 'streets-v2',
  satellite: 'hybrid',
  terrain: 'topo-v2',
};
const FALLBACK_STYLE_URL = 'https://demotiles.maplibre.org/style.json';
const MAP_STYLE_WARNING = 'MapLibre vector style fallback active. Set REACT_APP_MAPTILER_KEY in frontend/web/.env for production map styling.';

let warnedAboutMissingProviderKey = false;

function warnMissingProviderKey() {
  if (warnedAboutMissingProviderKey || process.env.NODE_ENV === 'test') {
    return;
  }

  warnedAboutMissingProviderKey = true;
  console.warn(MAP_STYLE_WARNING);
}

export function isMaplibreWebGLSupported() {
  const supportCheck = maplibregl?.supported || maplibregl?.isSupported;

  if (typeof supportCheck === 'function') {
    try {
      return Boolean(supportCheck({ failIfMajorPerformanceCaveat: true }));
    } catch (error) {
      console.warn('MapLibre support check failed, falling back to manual WebGL detection.', error);
    }
  }

  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return true;
  }

  try {
    const canvas = document.createElement('canvas');
    const contextOptions = { failIfMajorPerformanceCaveat: true };
    const webgl2 = canvas.getContext('webgl2', contextOptions);
    const webgl = webgl2 || canvas.getContext('webgl', contextOptions) || canvas.getContext('experimental-webgl', contextOptions);
    return Boolean(webgl);
  } catch (error) {
    console.warn('Manual WebGL detection failed.', error);
    return false;
  }
}

export function getMapStyleUrl(variant = 'default') {
  const styleId = MAPTILER_STYLE_IDS[variant] || MAPTILER_STYLE_IDS.default;

  if (!MAPTILER_KEY) {
    warnMissingProviderKey();
    return FALLBACK_STYLE_URL;
  }

  return `https://api.maptiler.com/maps/${styleId}/style.json?key=${MAPTILER_KEY}`;
}

export function createMapInstance({
  container,
  center,
  zoom,
  styleVariant = 'default',
  pitch = 0,
  bearing = 0,
  maxZoom = 19,
  minZoom = 2,
  ...overrides
}) {
  if (!isMaplibreWebGLSupported()) {
    throw new Error('WebGL indisponible ou degrade. MapLibre GL JS ne peut pas initialiser la carte correctement sur cet appareil.');
  }

  return new maplibregl.Map({
    container,
    style: getMapStyleUrl(styleVariant),
    center,
    zoom,
    pitch,
    bearing,
    maxZoom,
    minZoom,
    antialias: true,
    attributionControl: true,
    failIfMajorPerformanceCaveat: true,
    dragRotate: false,
    pitchWithRotate: false,
    touchPitch: false,
    ...overrides,
  });
}

export function createPricePillMarkerElement(priceLabel, isSelected) {
  const estimatedWidth = Math.min(Math.max(priceLabel.length * 8.1 + 26, 56), 132);
  const element = document.createElement('div');
  element.className = 'price-pill-marker';
  element.style.cssText = [
    'display:flex',
    'align-items:center',
    'justify-content:center',
    `min-width:${estimatedWidth}px`,
    'height:34px',
    'padding:0 14px',
    'border-radius:999px',
    'font:700 13px/1.1 "Segoe UI", sans-serif',
    'letter-spacing:0.01em',
    'backdrop-filter:blur(10px)',
    'cursor:pointer',
    'user-select:none',
    'transform-origin:center center',
    `color:${isSelected ? '#fff7ed' : '#111827'}`,
    `background:${isSelected
      ? 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)'
      : 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.96) 100%)'}`,
    `border:${isSelected ? '1px solid rgba(191,219,254,0.34)' : '1px solid rgba(148,163,184,0.32)'}`,
    `box-shadow:${isSelected
      ? '0 12px 32px rgba(29,78,216,0.34)'
      : '0 8px 24px rgba(15,23,42,0.16)'}`,
  ].join(';');
  element.textContent = priceLabel;
  return element;
}

export function createPinMarkerElement({
  accentColor = '#2563eb',
  innerColor = '#ffffff',
  size = 22,
  shadowColor = 'rgba(37,99,235,0.28)',
}) {
  const element = document.createElement('div');
  const haloSize = size + 14;
  element.style.cssText = `
    position:relative;
    width:${haloSize}px;
    height:${haloSize}px;
    display:flex;
    align-items:center;
    justify-content:center;
    cursor:pointer;
  `;

  const halo = document.createElement('div');
  halo.style.cssText = `
    position:absolute;
    inset:0;
    border-radius:999px;
    background:${shadowColor};
    filter:blur(8px);
    transform:scale(0.92);
  `;

  const pin = document.createElement('div');
  pin.style.cssText = `
    position:relative;
    width:${size}px;
    height:${size}px;
    border-radius:999px;
    background:linear-gradient(135deg, ${accentColor} 0%, #0f172a 100%);
    border:3px solid rgba(255,255,255,0.96);
    box-shadow:0 10px 24px rgba(15,23,42,0.24);
  `;

  const dot = document.createElement('div');
  const dotSize = Math.max(6, Math.round(size / 3.4));
  dot.style.cssText = `
    position:absolute;
    top:50%;
    left:50%;
    width:${dotSize}px;
    height:${dotSize}px;
    border-radius:999px;
    background:${innerColor};
    transform:translate(-50%, -50%);
  `;

  pin.appendChild(dot);
  element.appendChild(halo);
  element.appendChild(pin);
  return element;
}

export function createUserLocationMarkerElement() {
  return createPinMarkerElement({
    accentColor: '#0ea5e9',
    innerColor: '#e0f2fe',
    size: 18,
    shadowColor: 'rgba(14,165,233,0.30)',
  });
}

export const MAP_PROVIDER_ENV_NAME = 'REACT_APP_MAPTILER_KEY';
export const MAP_PROVIDER_SETUP_HINT = 'Create a free MapTiler key and put it in frontend/web/.env as REACT_APP_MAPTILER_KEY=...';